<template>
  <th
    :id="th.key"
    ref="tableHeader"
    :data-test="th.key"
    class="main-table-th"
    :class="[
      {
        'text-center': isTextCentered,
        fixed: th.fixed,
        'has-max-selected': maxSelected !== undefined,
      },
      th.key,
    ]"
    :style="{
      'text-align': th.textAlign,
    }"
    @click="emitAction(action, th)">
    <span
      v-if="th.title && !th.showIconOnly"
      :class="{
        pointer: th.selectable || th.orderable,
        'default-pointer': selectAllOnPage,
      }">
      {{ th.title }}
    </span>
    <template v-else-if="th.key === 'selectColumn'">
      <MdtCheckbox
        v-if="maxSelected === undefined"
        ref="bulkCheckbox"
        v-model="selectAllOnPage" />
      <span v-else-if="maxSelected !== 1">
        <div class="btn btn-basic btn-size-32 no-hover">
          {{ selectedRows.length }} / {{ maxSelected }}
        </div>
      </span>
      <!--
        Bulk select all UI - shown when table header actions are disabled.
        Displays selection count and allows selecting/deselecting all entries.
      -->
      <div
        v-if="!isTableHeaderActionAvailable"
        class="bulk-select-all"
        :class="{ 'bulk-select-all-sticky': isSticky }">
        <!-- Not all entries selected: show current count + "select all" action -->
        <template v-if="!allEntriesSelected">
          <span>{{ msg.bulk_page_entries_label }}</span>
          <span @click="selectAllEntries">{{ msg.bulk_page_all_entries_label }}</span>
        </template>
        <!-- All entries selected: show confirmation + "cancel" action -->
        <template v-else>
          <span>{{ msg.bulk_page_all_entries_label_selected }}</span>
          <span @click="resetSelection">{{ 'admin_cancel_selection' | translate }}</span>
        </template>
      </div>
    </template>
    <i
      v-else-if="th.icon && th.showIconOnly"
      :class="`${th.icon} ${th.orderable ? 'pointer' : ''}`" />
    <i
      v-if="sort && sort.key === th.key"
      :class="`fas fa-caret-${sort.order === 'asc' ? 'down' : 'up'}`"
      class="sort-icon" />
  </th>
</template>

<script>
export default {
  name: 'TableTh',
  props: {
    th: {
      type: Object,
      required: true,
    },
    tableId: {
      type: String,
      default: '',
    },
    sort: {
      type: Object,
      default: () => ({}),
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    maxSelected: {
      type: Number,
      default: undefined,
    },
    tableBody: {
      type: Array,
      default: () => [],
    },
    textCenteredColumns: {
      type: Array,
      required: true,
    },
    isTableHeaderActionAvailable: {
      type: Boolean,
      default: true,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let action = '';
    if (this.th.selectable) action = 'select';
    else if (this.th.orderable) action = 'sort';
    else if (this.th.favoritable) action = 'favoritize';
    const { translate } = this.$options.filters;

    return {
      action,
      translate,
    };
  },
  computed: {
    selectAllOnPage: {
      get() {
        // filter only through non-readonly rows
        // eslint-disable-next-line no-underscore-dangle
        const tBody = this.tableBody;
        const tSelected = this.selectedRows;
        if (!tSelected.length) return false;
        return tBody.every((tb) => {
          const selectedIndex = tSelected
            // eslint-disable-next-line no-underscore-dangle
            .findIndex((selected) => (selected._id === tb._id));
          return selectedIndex > -1;
        });
      },
      set(checked) {
        // filter only through non-readonly rows
        /* eslint-disable */
        const tBody = this.tableBody;
        const tSelected = this.selectedRows;
        tBody.forEach((tb) => {
          const selectedIndex = tSelected
            .findIndex((selected) => (selected._id === tb._id));

          if (checked && selectedIndex === -1) {
            this.selectedRows.push(tb);
          } else if (!checked && selectedIndex > -1) {
            this.selectedRows.splice(selectedIndex, 1);
          }
        });
        /* eslint-enable */
      },
    },
    isTextCentered() {
      return this.th.icon || this.textCenteredColumns.includes(this.th.key);
    },
    pagination() {
      return this.$store.getters['table/pagination'];
    },
    msg() {
      const { totalItems } = this.pagination;
      return {
        bulk_page_entries_label: this.translate('bulk_page_entries_label')
          .replace('[d]', this.selectedRows.length),
        bulk_page_all_entries_label: this.translate('bulk_page_all_entries_label')
          .replace('[d]', totalItems),
        bulk_page_all_entries_label_selected: this.translate('bulk_page_all_entries_selected_label')
          .replace('[d]', totalItems),
      };
    },
    isBackgroundProcessesActive() {
      const { settings } = this.$store.getters.bootstrap;
      return settings?.backgroundProcesses?.bulkMail;
    },
    allEntriesSelected() {
      return this.$store.getters['table/getAllEntriesSelected'];
    },
  },
  watch: {
    selectAllOnPage: {
      deep: true,
      handler() {
        this.$emit('handleSelectAllOnPage', this.selectAllOnPage);
      },
    },
  },
  mounted() {
    if (this.allEntriesSelected && !this.tableId && this.isBackgroundProcessesActive) {
      this.selectAllOnPage = true;
    }
  },
  methods: {
    emitAction(action, th) {
      if (!this.isTableHeaderActionAvailable) return;
      this.$emit(action, th);
    },
    selectAllEntries() {
      this.$store.dispatch('table/setAllEntriesSelected', true);
    },
    resetSelection() {
      this.$emit('selectAllEntries', false);
      const { bulkCheckbox } = this.$refs;
      if (bulkCheckbox) {
        bulkCheckbox.$emit('change', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-table-th {
  position: relative;

  ::v-deep {
    .mdt-checkbox label:before,
    .mdt-checkbox label:after {
      border-color: $color-text-secondary;
    }
  }
}

.sort-icon {
  padding-left: 8px;
}

.bulk-select-all {
  position: absolute;
  bottom: 15px;
  left: 50px;
  display: flex;
  gap: 16px;
  color: $color-text-primary;
  cursor: pointer;

   span:last-child {
     color: $color-text-important;
   }
}

.bulk-select-all-sticky {
  bottom: 0;
}

</style>
